import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, ShoppingCart, Clock, Leaf, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MealPlanningGuide() {
  const navigate = useNavigate();

  const mealIdeas = {
    breakfast: [
      { name: "Overnight Oats", description: "Rolled oats soaked in almond milk with chia seeds, berries, and honey", prep: "5 min (night before)" },
      { name: "Greek Yogurt Parfait", description: "Greek yogurt layered with granola, sliced almonds, and fresh fruit", prep: "3 min" },
      { name: "Veggie Egg Muffins", description: "Baked egg cups with spinach, peppers, and feta—make a batch on Sunday", prep: "25 min for 12" },
      { name: "Avocado Toast", description: "Whole grain bread with mashed avocado, everything seasoning, and a fried egg", prep: "7 min" },
      { name: "Smoothie Bowl", description: "Frozen berries, banana, spinach, and almond milk blended thick, topped with seeds", prep: "5 min" }
    ],
    lunch: [
      { name: "Mason Jar Salads", description: "Layered salads with dressing on bottom, greens on top—stays fresh for days", prep: "15 min for 5" },
      { name: "Buddha Bowls", description: "Quinoa, roasted vegetables, chickpeas, and tahini dressing", prep: "20 min" },
      { name: "Turkey Lettuce Wraps", description: "Ground turkey with Asian seasonings wrapped in butter lettuce", prep: "15 min" },
      { name: "Soup & Sandwich", description: "Homemade vegetable soup (batch cook) with half a whole grain sandwich", prep: "10 min" },
      { name: "Mediterranean Plate", description: "Hummus, cucumber, tomatoes, olives, whole wheat pita, and grilled chicken", prep: "10 min" }
    ],
    dinner: [
      { name: "Sheet Pan Salmon", description: "Salmon with roasted broccoli and sweet potatoes—all on one pan", prep: "30 min" },
      { name: "Slow Cooker Chicken", description: "Chicken breasts with salsa, black beans, and corn—set it and forget it", prep: "10 min + 6 hrs" },
      { name: "Stir-Fry Night", description: "Whatever vegetables you have with tofu or shrimp over brown rice", prep: "20 min" },
      { name: "Turkey Taco Bowl", description: "Seasoned ground turkey over cauliflower rice with all the fixings", prep: "20 min" },
      { name: "Veggie Pasta Primavera", description: "Whole wheat pasta with sautéed seasonal vegetables and olive oil", prep: "25 min" }
    ]
  };

  const shoppingTips = [
    { category: "Produce", items: "Buy a mix of quick-spoiling (berries, greens) and long-lasting (carrots, apples) produce" },
    { category: "Proteins", items: "Keep frozen chicken breasts, salmon, and shrimp as backup; buy fresh for first 3 days" },
    { category: "Grains", items: "Stock quinoa, brown rice, oats, and whole wheat pasta—they last months" },
    { category: "Dairy/Alt", items: "Greek yogurt, eggs, cheese, and plant-based milk for smoothies" },
    { category: "Pantry", items: "Olive oil, canned beans, nut butters, honey, and your favorite seasonings" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
            Week 2 • Day 2 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Meal Planning Made Simple
          </h1>
          <p className="text-xl text-secondary-600">
            Your Complete Guide to Healthy Weekly Meal Prep
          </p>
        </div>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/0510036e9eaa951a52ddca82be3ad6b37a71cbb97fa8369d2f7711c06638061b.png"
            alt="Healthy Meal Prep"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
            Why Meal Planning Changes Everything
          </h2>
          <p className="text-secondary-700 mb-4">
            Decision fatigue is real. By 6 PM, after a full day of choices, the last thing you want to do is figure out dinner. That's when takeout wins. Meal planning eliminates this daily stress by making decisions in advance when your willpower is fresh.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {[
              { icon: Clock, title: "Save 3+ Hours Weekly", description: "Less time wondering, more time enjoying" },
              { icon: ShoppingCart, title: "Reduce Food Waste", description: "Buy only what you'll actually use" },
              { icon: Leaf, title: "Eat Healthier", description: "Planned meals beat impulsive choices" }
            ].map((benefit, index) => (
              <div key={index} className="text-center p-4 bg-green-50 rounded-xl">
                <benefit.icon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <h3 className="font-bold text-secondary-900 text-sm">{benefit.title}</h3>
                <p className="text-xs text-secondary-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Meal Ideas */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
            15 Healthy Whole Food Recipes
          </h2>
          
          {/* Breakfast */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-amber-600 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-600">☀️</span>
              5 Breakfast Ideas
            </h3>
            <div className="grid gap-3">
              {mealIdeas.breakfast.map((meal, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-semibold text-secondary-900">{meal.name}</span>
                    <span className="text-secondary-600 text-sm"> — {meal.description}</span>
                  </div>
                  <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-full whitespace-nowrap">{meal.prep}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Lunch */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-blue-600 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">🥗</span>
              5 Lunch Ideas
            </h3>
            <div className="grid gap-3">
              {mealIdeas.lunch.map((meal, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-semibold text-secondary-900">{meal.name}</span>
                    <span className="text-secondary-600 text-sm"> — {meal.description}</span>
                  </div>
                  <span className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-full whitespace-nowrap">{meal.prep}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dinner */}
          <div>
            <h3 className="text-lg font-bold text-purple-600 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">🍽️</span>
              5 Dinner Ideas
            </h3>
            <div className="grid gap-3">
              {mealIdeas.dinner.map((meal, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-semibold text-secondary-900">{meal.name}</span>
                    <span className="text-secondary-600 text-sm"> — {meal.description}</span>
                  </div>
                  <span className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded-full whitespace-nowrap">{meal.prep}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Weekly Planner */}
        <section className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900">
              Your Weekly Planning Template
            </h2>
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
            <h3 className="font-bold text-secondary-900 mb-4">Sunday Planning Session (30 minutes)</h3>
            <ol className="space-y-3">
              {[
                "Review your week: What days are busy? When can you cook? When do you need quick meals?",
                "Choose 5 breakfasts, 5 lunches, 5 dinners from the ideas above (or your own favorites)",
                "Write them on a weekly calendar—be specific about which day",
                "Create your shopping list organized by store section (produce, proteins, dairy, pantry)",
                "Go shopping OR place an online grocery order for pickup"
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-green-700 font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className="text-secondary-700">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-bold text-secondary-900 mb-4">Smart Shopping List Categories</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {shoppingTips.map((tip, index) => (
                <div key={index} className="p-3 bg-green-50 rounded-lg">
                  <span className="font-semibold text-green-700">{tip.category}:</span>
                  <span className="text-secondary-600 text-sm"> {tip.items}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pro Tips */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
            Meal Prep Pro Tips for Busy Women
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Batch Cook Grains", tip: "Cook a big pot of quinoa or rice on Sunday. It keeps for 5 days and works for any meal." },
              { title: "Prep Once, Eat Twice", tip: "Make double portions at dinner. Tomorrow's lunch is already done." },
              { title: "Frozen is Your Friend", tip: "Frozen vegetables are just as nutritious and won't go bad. Keep bags of spinach, broccoli, and berries." },
              { title: "Theme Nights", tip: "Taco Tuesday, Stir-Fry Friday—themes simplify decisions and make planning automatic." },
              { title: "Breakfast for Dinner", tip: "Eggs and veggies make a perfectly healthy 15-minute dinner. Don't overthink it." },
              { title: "Embrace Imperfection", tip: "A 'B+' meal you actually eat beats an 'A+' meal you never make. Progress over perfection." }
            ].map((item, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-xl">
                <h3 className="font-bold text-secondary-900 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.tip}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default MealPlanningGuide;
